import { Injectable } from '@angular/core';
import { Settings as LuxonSettings } from 'luxon';
import type { Locales, TranslationFunctions } from '../../gensrc/i18n/i18n-types';
import { i18nObject } from '../../gensrc/i18n/i18n-util';
import { loadLocale } from '../../gensrc/i18n/i18n-util.sync';
import { BOOT_LOCALE } from './ui-language';

/**
 * Type-safe i18n service for Angular
 *
 * Usage in components:
 * ```typescript
 * import { LL } from '../i18n/i18n.service';
 *
 * // In template:
 * {{ LL.meals.breakfast() }}
 *
 * // In TypeScript:
 * const msg = LL.meals.breakfast();
 * ```
 */
// The UI language for THIS app session, resolved once synchronously at module
// load from the device-scoped `cs_ui_language` key (validated against the
// generated locale set, falling back to `en`). Switching language writes the
// key and reloads the app, so this value is stable for the whole session — see
// design-docs/i18n-translation.md. Re-exported so the sign-in reconciliation can
// compare against the locale `LL` is ACTUALLY bound to (a later re-read of the
// device key can differ from this, e.g. after the first-run default writes it).
//
// It is DEFINED in ./ui-language (the Angular-free module) so non-Angular
// helpers such as services/text-locale.ts can share the one captured value;
// this re-export keeps the app's existing `from 'i18n/i18n.service'` imports
// working.
export { BOOT_LOCALE } from './ui-language';

// Defensive global default for Luxon (#4204). Every date-display helper passes
// its locale EXPLICITLY, so nothing relies on this — it only localizes any stray
// Luxon call that slips past the formatting-boundary guard. It is safe to set
// once here because switching UI language reloads the whole app
// (reloadAppAsync → location.reload()), so BOOT_LOCALE is fixed for the session.
// See design-docs/date-time-localization.md.
LuxonSettings.defaultLocale = BOOT_LOCALE;

@Injectable({ providedIn: 'root' })
export class I18nService {
  private _LL!: TranslationFunctions;

  constructor() {
    this.initLocale(BOOT_LOCALE);
  }

  private initLocale(locale: Locales): void {
    loadLocale(locale);
    this._LL = i18nObject(locale);
  }

  get LL(): TranslationFunctions {
    return this._LL;
  }
}

// Global LL instance for convenience.
// Initialize immediately so it's available at module load time, bound to the
// booted locale. Any key not translated in that locale renders English because
// the generated catalog is merged keyed off en (see the sync script).
loadLocale(BOOT_LOCALE);
export const LL: TranslationFunctions = i18nObject(BOOT_LOCALE);
